import React, { useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { annotateSellerGroup } from '../../util/custom/cartAvailability';
import {
  selectCart,
  selectCartInitialized,
  setCartQuantity,
  removeListingFromCart,
  restoreCartItem,
  verifyCartLocks,
} from '../../ducks/cart.duck';
import { fetchCartListings } from './CartPage.duck';

import {
  H3,
  H4,
  Page,
  LayoutSingleColumn,
  NamedLink,
  IconAlert,
  CartSellerGroup,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './CartPage.module.css';

/**
 * CL-CART-01 — the cart page (requirements 4, 5, 7 and 9).
 *
 * Everything shown here is recomputed from the live listings. The cart's stored snapshots
 * paint a row before the listing arrives and are never used for money or availability.
 */
export const CartPageComponent = props => {
  const {
    groups = [],
    fetchInProgress,
    fetchError = null,
    scrollingDisabled,
    onSetQuantity,
    onRemoveItem,
    onCheckout = () => null,
  } = props;
  const intl = useIntl();

  const isEmpty = groups.length === 0;
  const title = intl.formatMessage({ id: 'CartPage.title' });
  // Units, not rows, so this figure and the header badge cannot disagree — the badge has
  // always counted units, and two counts of "your cart" differing by a quantity is the kind
  // of small wrongness that makes a buyer distrust the total underneath it.
  const itemCount = groups.reduce(
    (n, g) => n + (g.items || []).reduce((m, i) => m + (i.quantity || 0), 0),
    0
  );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.content}>
          {/* "Keep shopping" sits beside the heading, not under the panels. As a footer
              link it read as a leftover; here it is the obvious counterpart to the title.
              The count sits beside it too, because how much is in the cart is the first
              question a buyer has about one. */}
          <div className={css.header}>
            <div className={css.headingGroup}>
              <H3 as="h1" className={css.heading}>
                <FormattedMessage id="CartPage.heading" />
              </H3>
              {isEmpty ? null : (
                <span className={css.headingCount}>
                  <FormattedMessage
                    id="CartPage.itemCount"
                    values={{ items: itemCount, sellers: groups.length }}
                  />
                </span>
              )}
            </div>
            <NamedLink name="SearchPage" className={css.keepShopping}>
              <FormattedMessage id="CartPage.keepShopping" />
            </NamedLink>
          </div>

          {isEmpty ? (
            <div className={css.empty}>
              <svg className={css.emptyIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M6 6h15l-1.5 9h-12L6 6Zm0 0L5 3H2m6.5 17a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <H4 as="h2" className={css.emptyTitle}>
                <FormattedMessage id="CartPage.emptyTitle" />
              </H4>
              <p className={css.emptyBody}>
                <FormattedMessage id="CartPage.empty" />
              </p>
              <NamedLink name="SearchPage" className={css.emptyAction}>
                <FormattedMessage id="CartPage.emptyAction" />
              </NamedLink>
            </div>
          ) : (
            <>
              {/* The availability check failed. Said once, here, because the alternative is
                  every row claiming to be gone: a missing listing is how a deleted listing
                  and an unread one both look, and only this knows which happened. The rows
                  below stay pending — snapshot data, no verdict — and checkout stays
                  disabled, because nothing may be bought on data we could not verify. */}
              {fetchError ? (
                <p className={css.fetchError}>
                  <IconAlert className={css.fetchErrorIcon} />
                  <FormattedMessage id="CartPage.fetchError" />
                </p>
              ) : null}

              {/* Only when it is true. Stated once here rather than repeated in every
                  panel, and absent entirely for the single-seller cart that is every cart
                  today. */}
              {groups.length > 1 ? (
                <p className={css.multiSellerNotice}>
                  <IconAlert className={css.multiSellerNoticeIcon} />
                  <FormattedMessage
                    id="CartPage.multiSellerNotice"
                    values={{ count: groups.length }}
                  />
                </p>
              ) : null}

              <div className={css.groups}>
                {/* One panel per seller, each with its own subtotal and its own checkout.
                    Section M puts mixed-seller checkout in one payment out of scope, so a
                    cart-wide total and a single button would be a figure nobody can pay and
                    a button that cannot say what it buys. */}
                {groups.map(group => (
                  <CartSellerGroup
                    key={group.authorId}
                    group={group}
                    fetchInProgress={fetchInProgress}
                    onSetQuantity={onSetQuantity}
                    onRemoveItem={onRemoveItem}
                    onCheckout={onCheckout}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

// AC-12: "an Undo option is offered for at least 10 seconds". Ten exactly is the floor, so
// the toast outlives the global 4s default that every other cart toast uses.
export const UNDO_WINDOW_MS = 10000;

/**
 * AC-12 — removal has to be reversible for at least ten seconds.
 *
 * Exported so it can be tested on its own. Inline in the container it was unreachable: the
 * page's tests render CartPageComponent, which sits below this, so the whole Undo path would
 * have had no test at all.
 *
 * The removed row arrives FROM the thunk, because once the reducer has run it no longer
 * exists to rebuild — and rebuilding it from the live listing would lose the buyer's chosen
 * quantity and the snapshots that let a row paint before its listing loads.
 */
export const makeRemoveWithUndo = ({ onRemoveItem, onRestoreItem, intl }) => args =>
  onRemoveItem(args).then(removed => {
    if (!removed) return removed;
    const toastId = `cart-undo-${removed.listingId}`;
    toast(
      <span className={css.undoToast}>
        <FormattedMessage
          id="CartPage.removedToast"
          values={{ title: removed.titleSnapshot || '' }}
        />
        <button
          type="button"
          className={css.undoButton}
          onClick={() => {
            onRestoreItem({ authorId: args.authorId, item: removed });
            toast.dismiss(toastId);
          }}
        >
          {intl.formatMessage({ id: 'CartPage.undo' })}
        </button>
      </span>,
      {
        toastId,
        autoClose: UNDO_WINDOW_MS,
        // Otherwise a stray click anywhere on the toast dismisses the only way back.
        closeOnClick: false,
      }
    );
    return removed;
  });

export const EnhancedCartPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const {
    cart,
    cartInitialized,
    listings,
    listingsResolved,
    history,
    onFetchListings,
    onRemoveItem,
    onRestoreItem,
    onVerifyLocks,
    ...rest
  } = props;

  // Task 12: one seller per checkout (section M — mixed-seller checkout in one payment is
  // out of scope), so the button only ever needs to say which seller's panel it came from.
  const onCheckout = ({ authorId }) =>
    history.push(pathByRouteName('CartCheckoutPage', routeConfiguration, { authorId }));

  // Fetched here rather than in loadData: the cart lives in browser storage, so the server
  // does not know what is in it, and initializeCart has to have run first. Re-runs when the
  // cart changes so a removed row stops being fetched.
  const cartKey = JSON.stringify(
    Object.values(cart?.sellers || {}).flatMap(g => g.items.map(i => i.listingId))
  );
  useEffect(() => {
    if (cartInitialized) {
      onFetchListings();
      // CL-NEG-01 D.2 step 7 — reconcile a lock whose negotiation has closed elsewhere. Same
      // effect as the listings re-check, on purpose: both exist to keep this page honest about
      // what the buyer may currently do with each row.
      onVerifyLocks();
    }
  }, [cartInitialized, cartKey, onFetchListings, onVerifyLocks]);

  const listingsById = listings.reduce((acc, l) => ({ ...acc, [l.id.uuid]: l }), {});
  const listingTypes = config.listing.listingTypes || [];

  const groups = Object.values(cart?.sellers || {}).map(group =>
    annotateSellerGroup(group, listingsById, {
      // No delivery address on this page, so shipping stays "calculated at checkout"
      // (AC-18). The country arrives with the address on the checkout page.
      destinationCountry: null,
      listingTypes,
      // Until the fetch has succeeded, a row with no listing has not been read rather than
      // deleted. Without this the first paint of every visit accused the whole cart of being
      // unavailable, and a failed fetch left it saying so for good.
      listingsResolved,
    })
  );

  const onRemoveWithUndo = makeRemoveWithUndo({ onRemoveItem, onRestoreItem, intl });

  return (
    <CartPageComponent
      {...rest}
      groups={groups}
      onRemoveItem={onRemoveWithUndo}
      onCheckout={onCheckout}
    />
  );
};

const mapStateToProps = state => ({
  cart: selectCart(state),
  cartInitialized: selectCartInitialized(state),
  listings: state.CartPage.listings,
  listingsResolved: state.CartPage.listingsResolved,
  fetchInProgress: state.CartPage.fetchInProgress,
  fetchError: state.CartPage.fetchError,
  scrollingDisabled: isScrollingDisabled(state),
});

const mapDispatchToProps = dispatch => ({
  onFetchListings: () => dispatch(fetchCartListings()),
  onSetQuantity: args => dispatch(setCartQuantity(args)),
  onRemoveItem: args => dispatch(removeListingFromCart(args)),
  onRestoreItem: args => dispatch(restoreCartItem(args)),
  onVerifyLocks: () => dispatch(verifyCartLocks()),
});

const CartPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedCartPage);

export default CartPage;
